import { ADDON_MODULE_KEYS, MODULE_KEYS, type ModuleKey } from '@/modules/module-keys'

import type {
  ActivityEntry,
  CatalogItem,
  CatalogSchema,
  Collection,
  CollectionItem,
  Company,
  CreditBalance,
  Entitlement,
  Experience3d,
  Recommendation,
  SearchHistoryEntry,
  ShareDocument,
} from '../types'
import { DEMO_IMAGES } from './demo-images'

/* ==========================================================================
   Archivio in memoria
   --------------------------------------------------------------------------
   Sostituisce il backend finché non esiste. Non è un mucchio di costanti:
   applica le stesse regole che applicherà il server — scala i crediti, rifiuta
   le operazioni sui moduli spenti, scrive il registro attività — così
   l'interfaccia viene sviluppata contro un comportamento realistico e non
   contro una versione ottimista del mondo.
   ========================================================================== */

/** Quanto costa ogni operazione. Il server è l'autorità: il client legge e basta. */
export const CREDIT_COSTS = {
  ricerca_testo: 1,
  ricerca_immagine: 2,
  crea_collection: 1,
  aggiungi_a_collection: 1,
  crea_documento: 3,
  genera_esperienza_3d: 5,
  accetta_raccomandato: 1,
  chiedi_raccomandati: 1,
} as const

export type CreditAction = keyof typeof CREDIT_COSTS

const CREDIT_ACTION_MODULE: Record<CreditAction, ModuleKey> = {
  ricerca_testo: 'ricerca',
  ricerca_immagine: 'ricerca',
  crea_collection: 'collection',
  aggiungi_a_collection: 'collection',
  crea_documento: 'presentazione',
  genera_esperienza_3d: 'presentazione',
  accetta_raccomandato: 'recommender',
  chiedi_raccomandati: 'recommender',
}

const CREDIT_ACTION_LABEL: Record<CreditAction, string> = {
  ricerca_testo: 'Ricerca per testo',
  ricerca_immagine: 'Ricerca per immagine',
  crea_collection: 'Creazione collection',
  aggiungi_a_collection: 'Aggiunta a collection',
  crea_documento: 'Creazione documento',
  genera_esperienza_3d: 'Generazione esperienza 3D',
  accetta_raccomandato: 'Suggerimento accettato',
  chiedi_raccomandati: 'Nuovi suggerimenti',
}

export interface MockDatabase {
  items: CatalogItem[]
  collections: Collection[]
  documents: ShareDocument[]
  experiences: Experience3d[]
  entitlements: Entitlement[]
  credits: CreditBalance
  activity: ActivityEntry[]
  searchHistory: SearchHistoryEntry[]
  recommendations: Recommendation[]
  companies: Company[]
}

let sequence = 0
function nextId(prefix: string): string {
  sequence += 1
  return `${prefix}_${sequence.toString().padStart(4, '0')}`
}

function minutesAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString()
}

/**
 * I metadati finti, e il registro che li descrive.
 *
 * Un cliente vero porta campi suoi: qui se ne inventano alcuni plausibili per
 * avere qualcosa da filtrare e da mostrare in sviluppo. La forma è quella che
 * arriva davvero da `GET /api/catalog/schema`, non una semplificazione — se
 * divergesse, il mock smetterebbe di provare qualcosa.
 */
const MOCK_CATEGORIES = ['marmo', 'legno', 'tessuto', 'laminato'] as const
const MOCK_FINISHES = ['opaco', 'lucido', 'spazzolato'] as const
const MOCK_COLORS = ['avorio', 'bruno', 'verde salvia', 'antracite', 'sabbia'] as const

export const MOCK_CATALOG_SCHEMA: CatalogSchema = {
  schemaVersion: 1,
  types: {
    image: {
      label: 'Immagini',
      // Come l'azienda demo reale: nessuna etichetta designata, quindi
      // l'interfaccia mostra l'id abbreviato.
      labelField: null,
      fields: [
        {
          key: 'categoria',
          label: 'Categoria',
          type: 'keyword',
          multi: false,
          filterable: true,
          display: true,
          values: [...MOCK_CATEGORIES],
        },
        {
          key: 'finitura',
          label: 'Finitura',
          type: 'keyword',
          multi: false,
          filterable: true,
          display: true,
          values: [...MOCK_FINISHES],
        },
        {
          key: 'colori',
          label: 'Colori',
          type: 'keyword',
          multi: true,
          filterable: true,
          display: true,
          values: [...MOCK_COLORS],
        },
        {
          key: 'larghezzaCm',
          label: 'Larghezza (cm)',
          type: 'integer',
          multi: false,
          filterable: true,
          display: true,
          min: 30,
          max: 320,
        },
        {
          key: 'note',
          label: 'Note',
          type: 'text',
          multi: false,
          filterable: false,
          display: true,
        },
      ],
    },
  },
}

function buildCatalog(): CatalogItem[] {
  // Una riga per immagine demo: abbastanza per riempire una griglia
  // credibile, senza inventare un archivio finto di mille voci.
  return DEMO_IMAGES.map((imageUrl, index) => ({
    id: `item_${index}`,
    type: 'image',
    meta: {
      categoria: MOCK_CATEGORIES[index % MOCK_CATEGORIES.length],
      finitura: MOCK_FINISHES[index % MOCK_FINISHES.length],
      colori: [
        MOCK_COLORS[index % MOCK_COLORS.length],
        MOCK_COLORS[(index + 2) % MOCK_COLORS.length],
      ],
      larghezzaCm: 30 + ((index * 17) % 291),
      note: `Elemento dimostrativo numero ${index + 1}.`,
    },
    imageUrl,
  }))
}

function buildRecommendations(items: CatalogItem[]): Recommendation[] {
  const reasons = [
    'Ricorre nelle collection che hai chiuso questa stagione.',
    'Fuori dalle tue categorie abituali: vale una prova.',
    'Vicino all’elemento che hai cercato per ultimo.',
    'Scelto spesso da chi lavora sugli stessi clienti.',
  ]

  return items.slice(0, 6).map((item, index) => ({
    id: nextId('rec'),
    itemId: item.id,
    meta: item.meta,
    imageUrl: item.imageUrl,
    reason: reasons[index % reasons.length],
  }))
}

function toCollectionItem(item: CatalogItem, addedAt: string): CollectionItem {
  return {
    id: nextId('citem'),
    itemId: item.id,
    type: item.type,
    meta: item.meta,
    imageUrl: item.imageUrl,
    addedAt,
  }
}

function buildCompanies(): Company[] {
  // 'admin' combacia con DEMO_USERNAME/DEMO_PASSWORD del login mock: chi entra
  // in modalità mock vede subito il pannello Aziende, senza doverlo simulare.
  return [
    {
      id: 'company_admin',
      username: 'admin',
      companyName: 'Marmio (demo)',
      email: 'admin@example.com',
      role: 'admin',
      plan: null,
      createdAt: minutesAgo(60 * 24 * 120),
      lastLogin: minutesAgo(4),
    },
    {
      id: 'company_1',
      username: 'azienda-demo-1',
      companyName: 'Azienda Demo 1',
      email: 'info@azienda-demo-1.example',
      role: 'customer',
      plan: 'pro',
      createdAt: minutesAgo(60 * 24 * 40),
      lastLogin: minutesAgo(60 * 24 * 2),
    },
    {
      id: 'company_2',
      username: 'azienda-demo-2',
      companyName: 'Azienda Demo 2',
      email: 'contatti@azienda-demo-2.example',
      role: 'customer',
      plan: 'base',
      createdAt: minutesAgo(60 * 24 * 20),
      lastLogin: null,
    },
  ]
}

function createSeed(): MockDatabase {
  const items = buildCatalog()

  // Una collection già avviata: aprire la dashboard su uno schermo vuoto
  // non direbbe niente di come funziona.
  const seedItems = [items[0], items[6], items[11]].filter((item): item is CatalogItem =>
    Boolean(item),
  )

  const collection: Collection = {
    id: nextId('col'),
    name: 'Editoriale Primavera',
    items: seedItems.map((item) => toCollectionItem(item, minutesAgo(180))),
    createdAt: minutesAgo(190),
    updatedAt: minutesAgo(180),
    draft: false,
  }

  const entitlements: Entitlement[] = MODULE_KEYS.map((module) => {
    const isAddon = (ADDON_MODULE_KEYS as readonly ModuleKey[]).includes(module)
    // Trends e Generativa partono spenti: la dashboard deve mostrare da subito
    // com'è fatta la differenza fra un modulo attivo e uno che non lo è.
    const activeByDefault = !isAddon || module === 'analitica' || module === 'recommender'
    return {
      module,
      active: activeByDefault,
      activatedAt: activeByDefault ? minutesAgo(60 * 24 * 30) : null,
    }
  })

  return {
    items,
    collections: [collection],
    documents: [],
    experiences: [],
    entitlements,
    credits: {
      used: 0,
      included: 500,
      ledger: [],
    },
    activity: [
      {
        id: nextId('act'),
        message: 'Collection "Editoriale Primavera" aperta con 3 elementi.',
        module: 'collection',
        createdAt: minutesAgo(180),
      },
    ],
    searchHistory: [],
    recommendations: buildRecommendations(items),
    companies: buildCompanies(),
  }
}

export const db: MockDatabase = createSeed()

/** Ripristina lo stato iniziale. Serve ai test, che devono partire puliti. */
export function resetDatabase(): void {
  const seed = createSeed()
  db.items = seed.items
  db.collections = seed.collections
  db.documents = seed.documents
  db.experiences = seed.experiences
  db.entitlements = seed.entitlements
  db.credits = seed.credits
  db.activity = seed.activity
  db.searchHistory = seed.searchHistory
  db.recommendations = seed.recommendations
  db.companies = seed.companies
}

export function logActivity(message: string, module: ModuleKey): void {
  db.activity.unshift({
    id: nextId('act'),
    message,
    module,
    createdAt: new Date().toISOString(),
  })
  db.activity = db.activity.slice(0, 60)
}

export function isModuleActive(module: ModuleKey): boolean {
  return db.entitlements.find((entitlement) => entitlement.module === module)?.active ?? false
}

export interface SpendResult {
  ok: boolean
  cost: number
  remaining: number
}

/**
 * Scala i crediti di un'operazione. Se non bastano non scala niente e lo dice:
 * un addebito parziale sarebbe peggio di un rifiuto.
 */
export function spendCredits(action: CreditAction): SpendResult {
  const cost = CREDIT_COSTS[action]
  const remaining = db.credits.included - db.credits.used

  if (cost > remaining) {
    return { ok: false, cost, remaining }
  }

  db.credits.used += cost
  db.credits.ledger.unshift({
    id: nextId('cr'),
    action: CREDIT_ACTION_LABEL[action],
    module: CREDIT_ACTION_MODULE[action],
    cost,
    createdAt: new Date().toISOString(),
  })

  return { ok: true, cost, remaining: remaining - cost }
}

export function makeId(prefix: string): string {
  return nextId(prefix)
}

export function makeShareUrl(kind: 'doc' | '3d'): string {
  const slug = Math.random().toString(36).slice(2, 8)
  return `app.tuodominio.it/${kind}/${slug}`
}

export { toCollectionItem }
